import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TableContent } from '../LayoutPage';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { styled } from 'styletron-react';
import { HeadingMedium } from 'baseui/typography';
import { Button, KIND, SIZE } from 'baseui/button';
import { Spinner } from 'baseui/spinner';
import { Search, Calendar, ExternalLink, Plus, BarChart3 } from 'lucide-react';
import { MantineDataTable } from '../../components/MantineDataTable';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useEvents, eventStatusOf, Event } from '../../hooks/useEvents';
import { S3Image } from '../../components/S3Image';
import { STATUS_META, FINISHED_META, formatEventDate, formatEventDates, listStatusOf, type EventListStatus } from './eventForm';
import { isPrivateEvent } from '../../utils/privateEvent';
import { useBusinessContext } from '../../context/BusinessContext';
import { EventStatusBadge } from './EventStatusBadge';
import { useFdCan } from '../../hooks/useFdCan';
import { useIsPhone } from '../../hooks/useIsPhone';
import { EventsListPhone } from './EventsListPhone';
import { PhoneLogo, type PhoneOptionGroup, type PhoneRow } from '../../components/phone/PhoneListParts';
import { useT } from '../../i18n/admin';
import { useTenantBranding } from '../../hooks/useTenantBranding';

const Container = styled('div', {
  backgroundColor: '#fff',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const Header = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px 0 24px',
});

const HeaderActions = styled('div', {
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
});

const ToolbarRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 24px',
});

const SearchWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: '1',
  maxWidth: '280px',
  backgroundColor: '#EBEBEB',
  border: '1px solid #EBEBEB',
  borderRadius: '20px',
  padding: '7px 14px',
});

const SearchInputEl = styled('input', {
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '14px',
  color: '#374151',
  flex: 1,
  width: '100%',
  '::placeholder': { color: '#9CA3AF' },
});

const Main = styled('div', {
  flex: 1,
  overflow: 'auto',
  padding: '0 24px 24px',
});

const EmptyState = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  color: '#6B7280',
  fontSize: '14px',
});

const ChipRow = styled('div', {
  display: 'flex',
  gap: '6px',
  flexWrap: 'wrap',
});

const Chip = styled('button', ({ $on }: { $on: boolean }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 12px',
  borderRadius: '999px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: $on ? 'var(--tenant-primary, #111827)' : '#E5E7EB',
  backgroundColor: $on ? 'color-mix(in srgb, var(--tenant-primary, #111827) 8%, white)' : '#fff',
  color: $on ? 'var(--tenant-primary, #111827)' : '#374151',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
}));

const ChipCount = styled('span', {
  fontSize: '11px',
  fontWeight: 600,
  color: '#6B7280',
});

const RowButton = styled('button', ({ $primary }: { $primary?: boolean }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '5px 10px',
  borderRadius: '8px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: $primary ? 'var(--tenant-primary, #111827)' : '#E5E7EB',
  backgroundColor: $primary ? 'var(--tenant-primary, #111827)' : '#fff',
  color: $primary ? '#fff' : '#374151',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}));

const WaitingText = styled('span', {
  fontSize: '12px',
  color: '#92400E',
  // SAM-1323 (E1): under the status, in as many short lines as it needs, so it does not set the table's width
  display: 'block',
  maxWidth: '150px',
  whiteSpace: 'normal',
  lineHeight: 1.25,
});

type StatusFilter = 'all' | EventListStatus;

const FILTERS: Array<{ key: StatusFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Drafts' },
  { key: 'published', label: STATUS_META.published.label },
  { key: 'finished', label: FINISHED_META.label },
  { key: 'archived', label: STATUS_META.archived.label },
];

export const EventsList = () => {
  const navigate = useNavigate();
  // SAM-944 (M5): the list is view only on a phone: no way to create, and a tap opens the read-only dashboard, not the editor.
  const isPhone = useIsPhone();
  const { t, lang } = useT();
  // SAM-1309 (U21): the name of whoever approves is the tenant's, not a fixed «Cap Cana»; the event's name follows the language.
  const { tenantName } = useTenantBranding();
  const waitingText = tenantName ? t('phoneLists.events.waiting', { tenant: tenantName }) : t('phoneLists.events.waitingApproval');
  const eventName = (e: Event) => (lang === 'es' ? e.name_es || e.name_en || e.name : e.name_en || e.name_es || e.name);
  const [searchParams, setSearchParams] = useSearchParams();
  // Event-dashboard-only users get a read-only list: they can open dashboards
  // but cannot create or edit events.
  const { isEventDashboardOnly, isEventApprover } = useUserPermissions();
  const canCreateEvent = useFdCan('events', 'create');
  const { data: events, isLoading } = useEvents();
  const { tenantBusinesses, availableBusinesses } = useBusinessContext();
  const businessNameOf = useMemo(
    () => new Map([...(availableBusinesses ?? []), ...(tenantBusinesses ?? [])].map((b) => [String(b.id), b.name] as const)),
    [availableBusinesses, tenantBusinesses],
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // SAM-714: create/edit moved to a full page. Old "?edit=<id>" links (the
  // dashboard used to hand back control that way) go straight to it.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId) return;
    if (isEventDashboardOnly) {
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
      return;
    }
    navigate(`/eventsad/${editId}`, { replace: true });
  }, [searchParams, setSearchParams, navigate, isEventDashboardOnly]);

  const searched = useMemo(() => {
    if (!events) return [];
    if (!searchQuery.trim()) return events;
    const q = searchQuery.toLowerCase();
    return events.filter(e =>
      (e.name || e.name_es || e.name_en || '').toLowerCase().includes(q) ||
      (e.description_es || e.short_description_es || '').toLowerCase().includes(q) ||
      (e.address || '').toLowerCase().includes(q)
    );
  }, [events, searchQuery]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = { all: searched.length, draft: 0, published: 0, finished: 0, archived: 0 };
    searched.forEach((e) => { c[listStatusOf(eventStatusOf(e), e)] += 1; });
    return c;
  }, [searched]);

  const filteredEvents = useMemo(
    () => (statusFilter === 'all' ? searched : searched.filter((e) => listStatusOf(eventStatusOf(e), e) === statusFilter)),
    [searched, statusFilter],
  );

  const openEvent = (ev: Event) => {
    if (!ev?.id) return;
    navigate(isEventDashboardOnly || isPhone ? `/eventsad/${ev.id}/dashboard` : `/eventsad/${ev.id}`);
  };

  const publicEventsUrl = `${window.location.origin}/events`;

  // SAM-1323 (U22, E1): the approved list: Event (thumbnail, name, business) · Dates · Visibility · Status · action.
  const columns = useMemo<MRT_ColumnDef<Event>[]>(() => [
    {
      id: 'event', header: 'EVENT', size: 320,
      accessorFn: (e) => eventName(e) || '',
      Cell: ({ row }) => {
        const ev = row.original;
        const name = eventName(ev) || '—';
        const business = ev.business_id ? businessNameOf.get(String(ev.business_id)) : undefined;
        const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '·';
        const fallback = <span style={{ fontSize: 13, fontWeight: 700, color: '#6B7280' }}>{initials}</span>;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <span style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 8, backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {ev.logo_url
                ? <S3Image imageKey={ev.logo_url} alt="" fallbackComponent={fallback} style={{ width: 40, height: 40, objectFit: 'cover' }} />
                : fallback}
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
              {business && <span style={{ display: 'block', fontSize: 12, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{business}</span>}
            </span>
          </div>
        );
      },
    },
    {
      id: 'dates', header: 'DATES', size: 200, accessorFn: (e) => e.start_date || '',
      Cell: ({ row }) => <span style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>{formatEventDates(row.original.start_date, row.original.end_date) || '—'}</span>,
    },
    {
      id: 'visibility', header: 'VISIBILITY', size: 120, accessorFn: (e) => (isPrivateEvent(e) ? 'Private' : 'Public'),
      Cell: ({ row }) => <span style={{ fontSize: '13px', color: '#374151' }}>{isPrivateEvent(row.original) ? 'Private' : 'Public'}</span>,
    },
    {
      id: 'status', header: 'STATUS', size: 180,
      accessorFn: (e) => listStatusOf(eventStatusOf(e), e),
      Cell: ({ row }) => {
        const ev = row.original;
        const waiting = !isEventDashboardOnly && eventStatusOf(ev) === 'draft' && !isEventApprover;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <EventStatusBadge status={listStatusOf(eventStatusOf(ev), ev)} />
            {waiting && <WaitingText>{waitingText}</WaitingText>}
          </div>
        );
      },
    },
    {
      id: 'actions', header: '', size: 200, enableSorting: false,
      Cell: ({ row }) => {
        const ev = row.original;
        const isDraft = eventStatusOf(ev) === 'draft';
        // Stop the row click (which opens the editor) for these buttons.
        const stop = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); };
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
            {!isEventDashboardOnly && isDraft && isEventApprover && (
              <RowButton $primary onClick={stop(() => navigate(`/eventsad/${ev.id}/preview`))}>Review</RowButton>
            )}
            <RowButton title="Event dashboard" onClick={stop(() => navigate(`/eventsad/${ev.id}/dashboard`))}>
              <BarChart3 size={13} /> Dashboard
            </RowButton>
          </div>
        );
      },
    },
  ], [isEventApprover, isEventDashboardOnly, navigate, waitingText, lang, businessNameOf]);

  // SAM-1308 (canvas PhoneEvents): on a phone the list is cards with ONE status dropdown, not the squeezed table.
  if (isPhone) {
    const toneOf = (s: EventListStatus): 'ok' | 'wait' | 'off' => (s === 'published' ? 'ok' : s === 'draft' ? 'wait' : 'off');
    const labelOf = (s: EventListStatus) => (s === 'finished' ? FINISHED_META.label : STATUS_META[s].label);
    const dayOf = (iso?: string) => formatEventDate(iso);
    const phoneRows: PhoneRow[] = filteredEvents.map((ev) => {
      const status = listStatusOf(eventStatusOf(ev), ev);
      const name = eventName(ev) || t('phoneLists.events.untitled');
      const start = dayOf(ev.start_date);
      const end = dayOf(ev.end_date);
      const sub = [
        ev.address,
        ev.capacity ? t('phoneLists.events.people', { n: ev.capacity }) : null,
        status === 'draft' && !isEventApprover ? waitingText : null,
      ].filter(Boolean).join(' · ');
      return {
        key: String(ev.id),
        when: start && end && start !== end ? `${start} – ${end}` : start || end || '',
        status: { label: labelOf(status), tone: toneOf(status) },
        name,
        sub: sub || undefined,
        avatar: (
          <PhoneLogo fallback={<Calendar size={20} className="text-[var(--ink-400)]" aria-hidden />}>
            {ev.logo_url ? <S3Image imageKey={ev.logo_url} alt="" /> : undefined}
          </PhoneLogo>
        ),
        open: () => openEvent(ev),
      };
    });
    const groups: PhoneOptionGroup[] = [{
      options: FILTERS.map((f) => ({ id: f.key, label: f.key === 'all' ? t('phoneLists.events.all') : f.label })),
    }];
    const chosen = groups[0].options.find((o) => o.id === statusFilter);
    const display = `${chosen?.label ?? ''} · ${counts[statusFilter].toLocaleString('en-US')}`;
    return (
      <EventsListPhone
        rows={phoneRows}
        loading={isLoading}
        query={searchQuery}
        onQuery={setSearchQuery}
        filter={statusFilter}
        display={display}
        groups={groups}
        onFilter={(id) => setStatusFilter(id as StatusFilter)}
        emptyText={searchQuery || statusFilter !== 'all' ? t('phoneLists.events.empty') : t('phoneLists.events.none')}
      />
    );
  }

  return (
    <TableContent>
      <Container>
        <Header>
          <HeadingMedium margin={0}>Events</HeadingMedium>
          <HeaderActions>
            <Button size={SIZE.compact} kind={KIND.secondary} startEnhancer={() => <ExternalLink size={14} />}
              onClick={() => window.open(publicEventsUrl, '_blank')}
              overrides={{ BaseButton: { style: { borderRadius: '8px', fontSize: '13px' } } }}>
              Public Page
            </Button>
            {!isEventDashboardOnly && canCreateEvent && !isPhone && (
              <Button size={SIZE.compact} startEnhancer={() => <Plus size={14} />} onClick={() => navigate('/eventsad/new')}
                overrides={{ BaseButton: { style: { borderRadius: '8px', fontSize: '13px' } } }}>
                New Event
              </Button>
            )}
          </HeaderActions>
        </Header>

        <ToolbarRow>
          <SearchWrapper>
            <Search size={16} color="#9CA3AF" />
            <SearchInputEl placeholder="Search events..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </SearchWrapper>
          <ChipRow role="group" aria-label="Filter by status">
            {FILTERS.map((f) => (
              <Chip key={f.key} type="button" $on={statusFilter === f.key} aria-pressed={statusFilter === f.key} onClick={() => setStatusFilter(f.key)}>
                {f.label} <ChipCount>{counts[f.key]}</ChipCount>
              </Chip>
            ))}
          </ChipRow>
        </ToolbarRow>

        <Main>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner /></div>
          ) : filteredEvents.length === 0 ? (
            <EmptyState>
              <Calendar size={40} color="#D1D5DB" style={{ marginBottom: 12 }} />
              <div>{searchQuery || statusFilter !== 'all' ? 'No matching events' : 'No events yet'}</div>
              {!searchQuery && statusFilter === 'all' && !isEventDashboardOnly && canCreateEvent && !isPhone && <Button size={SIZE.compact} onClick={() => navigate('/eventsad/new')} overrides={{ BaseButton: { style: { marginTop: '12px' } } }}>Create your first event</Button>}
            </EmptyState>
          ) : (
            <MantineDataTable columns={columns} data={filteredEvents} enablePagination enableSorting
              onRowClick={(row) => openEvent(row as Event)} />
          )}
        </Main>
      </Container>
    </TableContent>
  );
};
